export const WorkflowStepType = {
  Edit: 'Edit',
  Review: 'Review',
  Publish: 'Publish',
  Signature: 'Signature',
  Archive: 'Archive',
} as const;

export type WorkflowStepTypeCode = (typeof WorkflowStepType)[keyof typeof WorkflowStepType];

export const WORKFLOW_STEP_TYPES_ALL: readonly string[] = [
  WorkflowStepType.Edit,
  WorkflowStepType.Review,
  WorkflowStepType.Publish,
  WorkflowStepType.Signature,
  WorkflowStepType.Archive,
];

export const WorkflowActionCodes = {
  Save: 'Save',
  SendForReview: 'SendForReview',
  SendForSignature: 'SendForSignature',
  Publish: 'Publish',
  CompleteEdit: 'CompleteEdit',
  Comment: 'Comment',
  Approve: 'Approve',
  Reject: 'Reject',
  Sign: 'Sign',
  Archive: 'Archive',
  Edit: 'Edit',
} as const;

export type WorkflowActionCode = (typeof WorkflowActionCodes)[keyof typeof WorkflowActionCodes];

export const WORKFLOW_ACTION_CODES_ALL: readonly string[] = [
  WorkflowActionCodes.Save,
  WorkflowActionCodes.SendForReview,
  WorkflowActionCodes.SendForSignature,
  WorkflowActionCodes.Publish,
  WorkflowActionCodes.CompleteEdit,
  WorkflowActionCodes.Comment,
  WorkflowActionCodes.Approve,
  WorkflowActionCodes.Reject,
  WorkflowActionCodes.Sign,
  WorkflowActionCodes.Archive,
  WorkflowActionCodes.Edit,
];

export const WORKFLOW_STATE_TRANSITION_ACTIONS: readonly string[] = [
  WorkflowActionCodes.SendForReview,
  WorkflowActionCodes.SendForSignature,
  WorkflowActionCodes.Publish,
  WorkflowActionCodes.CompleteEdit,
  WorkflowActionCodes.Approve,
  WorkflowActionCodes.Reject,
  WorkflowActionCodes.Sign,
  WorkflowActionCodes.Archive,
  WorkflowActionCodes.Edit,
];

const ALL_ACTIONS_SET = new Set(WORKFLOW_ACTION_CODES_ALL.map(a => a.toLowerCase()));

const APPLICABLE_BY_STEP_TYPE: Readonly<Record<string, ReadonlySet<string>>> = {
  [WorkflowStepType.Edit]: new Set(
    [
      WorkflowActionCodes.Save,
      WorkflowActionCodes.SendForReview,
      WorkflowActionCodes.SendForSignature,
      WorkflowActionCodes.Publish,
      WorkflowActionCodes.CompleteEdit,
      WorkflowActionCodes.Comment,
    ].map(x => x.toLowerCase())
  ),
  [WorkflowStepType.Review]: new Set(
    [
      WorkflowActionCodes.Comment,
      WorkflowActionCodes.Approve,
      WorkflowActionCodes.Reject,
    ].map(x => x.toLowerCase())
  ),
  [WorkflowStepType.Signature]: new Set(
    [
      WorkflowActionCodes.Sign,
      WorkflowActionCodes.Reject,
      WorkflowActionCodes.Comment,
    ].map(x => x.toLowerCase())
  ),
  [WorkflowStepType.Publish]: new Set(
    [
      WorkflowActionCodes.Comment,
      WorkflowActionCodes.Edit,
      WorkflowActionCodes.Archive,
      WorkflowActionCodes.Publish,
    ].map(x => x.toLowerCase())
  ),
  [WorkflowStepType.Archive]: new Set(
    [WorkflowActionCodes.Archive, WorkflowActionCodes.Edit, WorkflowActionCodes.Comment].map(x => x.toLowerCase())
  ),
};

export function isKnownWorkflowAction(action: string | null | undefined): boolean {
  if (!action?.trim()) return false;
  return ALL_ACTIONS_SET.has(action.trim().toLowerCase());
}

export function isApplicableActionForStepType(stepType: string | null | undefined, action: string | null | undefined): boolean {
  if (!stepType?.trim() || !action?.trim()) return false;
  const set = APPLICABLE_BY_STEP_TYPE[stepType.trim()];
  if (!set) return false;
  return set.has(action.trim().toLowerCase());
}

export function getApplicableActionsForStepType(stepType: string): string[] {
  const set = APPLICABLE_BY_STEP_TYPE[stepType.trim()];
  if (!set) return [];
  return WORKFLOW_ACTION_CODES_ALL.filter(a => set.has(a.toLowerCase()));
}

/** Remove ações que não se aplicam ao tipo atual (ex.: Save herdado após trocar Edição → Arquivamento). */
export function filterAllowedActionsForStepType(
  stepType: string | null | undefined,
  actions: string[] | null | undefined
): string[] {
  if (!stepType?.trim()) return [];
  if (!actions?.length) return [];
  return actions.filter(a => a?.trim() && isApplicableActionForStepType(stepType, a));
}

export function isKnownWorkflowStepType(type: string | null | undefined): boolean {
  if (!type?.trim()) return false;
  return WORKFLOW_STEP_TYPES_ALL.some(t => t.localeCompare(type.trim(), undefined, { sensitivity: 'accent' }) === 0);
}

export function isSignatureStepType(type: string | null | undefined): boolean {
  if (!type?.trim()) return false;
  const n = type.trim();
  return n.localeCompare(WorkflowStepType.Signature, undefined, { sensitivity: 'accent' }) === 0;
}

export function isReviewPhaseStepType(type: string | null | undefined): boolean {
  if (!type?.trim()) return false;
  const n = type.trim();
  return n.localeCompare(WorkflowStepType.Review, undefined, { sensitivity: 'accent' }) === 0;
}

export function isEditStepType(type: string | null | undefined): boolean {
  if (!type?.trim()) return false;
  return type.trim().localeCompare(WorkflowStepType.Edit, undefined, { sensitivity: 'accent' }) === 0;
}

export function isPublishStepType(type: string | null | undefined): boolean {
  if (!type?.trim()) return false;
  return type.trim().localeCompare(WorkflowStepType.Publish, undefined, { sensitivity: 'accent' }) === 0;
}

export function isArchiveStepType(type: string | null | undefined): boolean {
  if (!type?.trim()) return false;
  return type.trim().localeCompare(WorkflowStepType.Archive, undefined, { sensitivity: 'accent' }) === 0;
}

export interface WorkflowStepTypePolicy {
  required: boolean;
  singleInstance: boolean;
}

/**
 * Ponto único de configuração da obrigatoriedade das etapas (espelho do backend `WorkflowStepPolicies`).
 * `required`: o fluxo deve possuir ao menos uma etapa desse tipo.
 * `singleInstance`: pode existir no máximo uma etapa desse tipo.
 * As regras de posicionamento continuam em `workflowDefinitionValidation.ts` / `workflowStepConstraints.ts`.
 */
export const WORKFLOW_STEP_POLICY: Readonly<Record<string, WorkflowStepTypePolicy>> = {
  [WorkflowStepType.Edit]: { required: true, singleInstance: false },
  [WorkflowStepType.Review]: { required: false, singleInstance: false },
  [WorkflowStepType.Signature]: { required: false, singleInstance: true },
  [WorkflowStepType.Publish]: { required: true, singleInstance: true },
  [WorkflowStepType.Archive]: { required: true, singleInstance: true },
};

export const CANONICAL_STEP_TYPES: readonly string[] = [
  WorkflowStepType.Edit,
  WorkflowStepType.Review,
  WorkflowStepType.Signature,
  WorkflowStepType.Publish,
  WorkflowStepType.Archive,
];

const STEP_TYPE_LABEL_PT: Readonly<Record<string, string>> = {
  [WorkflowStepType.Edit]: 'Edição',
  [WorkflowStepType.Review]: 'Revisão',
  [WorkflowStepType.Signature]: 'Assinatura',
  [WorkflowStepType.Publish]: 'Publicação',
  [WorkflowStepType.Archive]: 'Arquivamento',
};

/** Normaliza um tipo de etapa para o seu valor canônico (ou null se desconhecido). */
export function canonicalStepType(type: string | null | undefined): string | null {
  if (isReviewPhaseStepType(type)) return WorkflowStepType.Review;
  if (isSignatureStepType(type)) return WorkflowStepType.Signature;
  if (isEditStepType(type)) return WorkflowStepType.Edit;
  if (isPublishStepType(type)) return WorkflowStepType.Publish;
  if (isArchiveStepType(type)) return WorkflowStepType.Archive;
  return null;
}

export function getStepTypePolicy(type: string | null | undefined): WorkflowStepTypePolicy | null {
  const canonical = canonicalStepType(type);
  return canonical ? WORKFLOW_STEP_POLICY[canonical] : null;
}

export function isRequiredStepType(type: string | null | undefined): boolean {
  return getStepTypePolicy(type)?.required ?? false;
}

export function isSingleInstanceStepType(type: string | null | undefined): boolean {
  return getStepTypePolicy(type)?.singleInstance ?? false;
}

export function getStepTypeLabel(type: string | null | undefined): string {
  const canonical = canonicalStepType(type);
  return (canonical && STEP_TYPE_LABEL_PT[canonical]) || (type ?? '');
}

export const OFFICIAL_DOCUMENT_STATUSES = ['edit', 'review', 'signature', 'published', 'archived'] as const;

export type OfficialDocumentStatus = (typeof OFFICIAL_DOCUMENT_STATUSES)[number];

export function normalizeDocumentStatus(raw: string | null | undefined): OfficialDocumentStatus | string {
  const s = (raw || '').trim().toLowerCase();
  if (!s) return 'edit';
  if (s === 'pending_approval' || s === 'approved') return 'review';
  if (s === 'digital_signature') return 'signature';
  if (s === 'rejected') return 'edit';
  if ((OFFICIAL_DOCUMENT_STATUSES as readonly string[]).includes(s)) return s as OfficialDocumentStatus;
  return s;
}
